import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// IMPORTANTE: NON impostiamo axios.defaults.withCredentials = true
// globalmente, perché con CORS_ORIGINS=* in produzione il browser bloccherebbe
// TUTTE le chiamate (catalogo, carrello, ecc.). withCredentials lo passiamo
// solo dove serve davvero (login/logout/me).

// Interceptor: se ricevo 401 su route admin, pulisco la sessione lato client.
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || '';
    const status = err?.response?.status;
    if (status === 401 && (url.includes('/api/admin/') || url.includes('/api/auth/me'))) {
      sessionStorage.removeItem('cg_admin_token');
      delete axios.defaults.headers.common['Authorization'];
    }
    return Promise.reject(err);
  }
);

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  // user può essere: undefined = "checking", null = "unauthenticated", object = "authenticated"
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    // Se non c'è nessun token, non interroghiamo /me (evita un 401 inutile)
    const token = sessionStorage.getItem('cg_admin_token');
    if (!token) { setUser(null); return; }
    try {
      // NIENTE withCredentials: in produzione il CORS è wildcard, useremo solo
      // il Bearer header che funziona universalmente cross-domain.
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      sessionStorage.removeItem('cg_admin_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (username, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { username, password });
    // Salviamo SEMPRE il Bearer token in sessionStorage.
    if (data?.token) {
      sessionStorage.setItem('cg_admin_token', data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    }
    setUser({ username: data.username, role: data.role });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* ignore */
    }
    sessionStorage.removeItem('cg_admin_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ user, login, logout, refresh }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

// Ripristina il token in sessionStorage all'avvio (se presente)
const storedToken = typeof window !== 'undefined' ? sessionStorage.getItem('cg_admin_token') : null;
if (storedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
